import { isDefined } from "@clipboard-health/util-ts";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { formatDayOfMonth } from "@src/appV2/redesign/utils/formatDayOfMonth";
import { addHours, formatDistanceToNow, isBefore, parseISO } from "date-fns";
import { useHistory } from "react-router-dom";

import { useInHomeVisitModalsDataContext } from "../../ShiftDiscovery/useInHomeVisitModalsDataContext";
import { BOOKED_VISIT_CONFIRM_DATE_MODAL_PATH } from "../paths";
import { VisitBottomSheetInnerCard } from "../VisitBottomSheet/InnerCard/BaseInnerCard";

export function VisitStatusAlertCard(): JSX.Element | null {
  const { visit, generateModalPath } = useInHomeVisitModalsDataContext();
  const history = useHistory();
  const { attributes: visitAttributes } = visit ?? {};
  const { status, confirmedVisitDate, pendedAt, bookedAt } = visitAttributes ?? {};

  if (status === "FILLED" && isDefined(bookedAt)) {
    // @ts-expect-error - bookedAt is a string
    const parsedBookedAt = parseISO(bookedAt);
    const confirmationDeadline = addHours(parsedBookedAt, 24);
    const timeToConfirm = formatDistanceToNow(confirmationDeadline);

    if (isBefore(confirmationDeadline, new Date())) {
      return null;
    }

    return (
      <VisitBottomSheetInnerCard
        illustrationType="hours-limit-small"
        title={`Confirm or pend this visit within ${timeToConfirm} to avoid penalty`}
        intent="warning"
      />
    );
  }

  if (status === "PENDING" && isDefined(pendedAt)) {
    // @ts-expect-error - pendedAt is a string
    const parsedPendedAt = parseISO(pendedAt);
    const formattedDate = parsedPendedAt.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });

    return (
      <VisitBottomSheetInnerCard
        illustrationType="clock-in-pending-small"
        title="Your visit is marked as pending"
        description={`Marked on ${formattedDate}`}
        intent="neutral"
      />
    );
  }

  if (status === "CONFIRMED" && isDefined(confirmedVisitDate)) {
    // @ts-expect-error - confirmedVisitDate is a string
    const parsedConfirmedVisitDate = parseISO(confirmedVisitDate);
    const formattedDate = formatDayOfMonth(parsedConfirmedVisitDate);

    return (
      <VisitBottomSheetInnerCard
        illustrationType="shift-booked"
        title={`Your visit is confirmed for ${formattedDate}`}
        intent="success"
        description={
          <TextButton
            underline
            size="large"
            onClick={() => {
              if (!isDefined(visit)) {
                return;
              }

              history.push(
                generateModalPath(BOOKED_VISIT_CONFIRM_DATE_MODAL_PATH, {
                  caseId: visit.attributes.caseId,
                  visitId: visit.id,
                })
              );
            }}
          >
            Change date
          </TextButton>
        }
      />
    );
  }

  return null;
}
